<script lang="ts" setup>
import {
  useCreators,
  useAuth,
  useStorage,
  useAsyncData,
  useDialog,
  ref,
  onMounted,
} from '#imports';
const { fetchFeaturedCreators, hasApplied } = useCreators();
const { currentUser } = useAuth();
const { formatedImageUrl } = useStorage();

// const { data: featuredCreators } = await useAsyncData('featruredCreators', fetchFeaturedCreators)

const { data: creators } = await useAsyncData('allCreators', async () => {
  return await fetchFeaturedCreators(15);
});
const videoSource = '/media/videos/creator-video-hero.webm';

const { openDialog } = useDialog();
const creatorOnboarding = () => {
  openDialog('MPAuth', {
    title: 'Sign up & Apply',
    subtitle: 'Apply to become a Mappr Creator',
    startOn: 'signup',
    userType: 'creator',
  });
};

const applied = ref();

const isLoading = ref(true);
onMounted(async () => {
  isLoading.value = false;
  applied.value = await hasApplied(currentUser.value?.user?.id);

  if (currentUser.value?.profile?.is_creator) {
    applied.value = true;
  }
});
</script>

<template>
  <section>
    <div class="hero-wrapper">
      <div class="video-wrapper">
        <video playsinline autoplay muted loop poster="/media/videos/creator-video-hero-poster.jpg">
          <source :src="videoSource" type="video/webm">
        </video>
      </div>
      <MPTitleRolodex
        :words="['Supporters', 'Revenue', 'Community', 'Audience']"
        class="content"
        :first-line="false"
      >
        <template #title>
          <span class="first-title">Map your spots</span>
        </template>
        <template #titleContinued>
          <span class="second-title">grow your</span>&nbsp;
        </template>
        <template #summary>
          <span class="title-summary">Earn recurring revenue by sharing your curated lists and guides, empowered by the support of your followers</span>
        </template>
        <template #buttons>
          <MPButton v-show="!isLoading" :disabled="applied" size="large" :label="applied ? 'Application sent' : 'Sign up & Apply'" version="default" rounded @click="creatorOnboarding"/>&nbsp;
        </template>
      </MPTitleRolodex>
      <MPSkewContainer
        id="map-skew"
        image-src="/media/images/map-bcn-1x.jpg"
        class="map skew-graphic"
        animate
      />
      <MPSkewContainer
        id="outline-skew"
        stroke="var(--color-indigo-200)"
        class="outline skew-graphic"
      />
      <MPSkewContainer
        id="image-skew"
        video-src="media/videos/creator-video-hero.webm"
        class="fill skew-graphic"
      />
    </div>
    <div class="hero-callout">
      <h2>
        For those sharing their world's finest secrets; turn authentic knowledge into lasting value
      </h2>
    </div>
    <div v-if="creators" ref="carousel" class="carousel">
      <NuxtLinkLocale
        v-for="creator in creators"
        :key="creator.id"
        :to="`/@${creator.mappr_handle}`"
        :style="`background-image: url('${formatedImageUrl(creator.profile_image_url, 350, 400, 80)}')`"
        class="carousel-item"
      >
        <span class="carousel-item-title">
          {{ creator.display_name }}
        </span>
        <span class="carousel-item-handle">
          @{{ creator.mappr_handle }}
        </span>
        <span class="carousel-item-spots">
        {{ creator.maps.length }} {{ creator.maps.length === 1 ? 'Map' : 'Maps' }}
        </span>
        <!-- <span class="carousel-item-spots">
         {{ creator.maps.spots_count }} Spots
        </span> -->
      </NuxtLinkLocale>
    </div>
  </section>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/typography.pcss";
@import "@/assets/css/layouts.pcss";

.hero-callout {
  padding: 6rem 1rem 5rem;
  text-align: center;
  h2 {
    line-height: 1.45;
  }
  @mixin screen sm {
    display:none;
  }
}

.hero-wrapper {
  @mixin layout-wrapper-wide;
  margin: 0 auto;
  position: relative;
  display: grid;
  z-index: 1;
  position: relative;
  height: 110vh;
  min-height: 35rem;
  /* max-height: 40rem; */
  align-items: end;
  transition: height 0.5s ease-in;
  transition-delay: 0.25s;
  padding-top: 4rem;
  article {
    height: 100%;
    justify-content: center;
  }
  @mixin screen sm {
    article {
      height: 100%;
      justify-content: center;
    }
    height: 48rem;
    background: transparent;
    margin: 2vh auto;
    align-items: center;
  }
  @mixin screen xxl {
    margin: 4vh auto 6vh;
  }
}

.video-wrapper {
  background-color: var(--color-black);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  @mixin screen sm {
    display: none;
  }
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.80) 0.16%, rgba(0, 0, 0, 0.20) 38.16%, rgba(0, 0, 0, 0.85) 67.37%);
  }
}
.hero-cta-buttons {
  display: grid;
  gap: 0.5rem;
  @mixin screen sm {
    display: flex;
  }
  a:last-child button {
    color: white !important;
  }
  a:last-child button {
    @mixin screen sm {
      color: black !important;
    }
  }
  @mixin screen sm {
    display: inline-flex;
  }
}

.first-title {
  @mixin screen xs {
    margin-left: 0;
  }
}

.second-title {
  margin-left: -10rem;
  /* display: block; */
  margin-bottom: 0.25rem;
  @mixin screen xs {
    display: inline-flex;
    margin-left: -17rem;
  }
  @mixin screen sm {
    margin-left: 0;
  }
}

.content :deep(.title .highlight) {
  color: var(--color-indigo-400);
  @mixin screen sm {
    margin-left: 0;
  }
}

.content :deep(.title .highlight.active) {
  transform: translate(0, 0) !important;
}

.content :deep(.title .highlight.previous) {
  transform: translate(0, -25%) !important;
}

.content :deep(.title .highlight.next) {
  transform: translate(0, 100%) !important;

}
.content :deep(.title .highlight.before-next) {
  transform: translate(0, 200%) !important;
}

.map,
.fill-mobile {
  z-index: 0;
  position: absolute;
  overflow: hidden;
  height: auto;
  opacity: 0.5;
  top: -2rem;
  right: -6rem;
  width: 270px;
  @mixin screen xs {
    right: -2rem;
    top: 0;
    width: 440px;
  }
}

.map {
  display: none;
  @mixin screen xs {
    display: block;
  }
}

.fill-mobile {
  display: block;
  @mixin screen xs {
    display: none;
  }
}

.outline {
  z-index: 0;
  position: absolute;
  overflow: hidden;
  top: 2rem;
  right: -1rem;
  stroke-width: 1rem;
  width: 250px;
  @mixin screen xs {
    top: 5rem;
    right: 10rem;
    width: 330px;
  }
}

.fill {
  display: none;
  @mixin screen xs {
    display: block;
    position: absolute;
    top: 12rem;
    right: 5rem;
  }
}

.content {
  padding: 1rem 1rem 1rem;
  animation: fade-in 0.25s ease-in-out forwards;
  z-index: 10;
  margin: 0 auto;
  height: fit-content;
  text-align: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  @mixin screen sm {
    all: unset;
    display: flex;
    flex-direction: column;
    justify-content: center;
    z-index: 10;
    text-align: left;
    height: auto;
    background: linear-gradient(120deg, #FFF 35%, rgba(255, 255, 255, 0.00) 50%);
  }
  @mixin screen md {
    background: transparent;
  }
}


@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.carousel {
  width: 100%;
  display: inline-flex;
  gap: 1rem;
  overflow-x:scroll;
  padding-left: 1rem;
  scrollbar-width: none;
  margin-top: 0;
  z-index: 1;
  position: relative;
  a:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-0.25rem);
    transition: all 0.2s ease-in-out;
  }
}

.carousel-item {
  min-width: 15rem;
  min-height: 20rem;
  background-size: cover;
  background-position: center center;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: end;
  color: white;
  padding: 1rem;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0;
  transition: all 0.2s ease-in-out;
  transition: all 0.2s ease-in-out;
  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-0.15rem);
    transition: all 0.2s ease-in-out;
  }
  @mixin screen xs {
    min-width: 18rem;
    min-height: 22rem;
  }
}

.carousel-item:before {
  position: absolute;
  content: "";
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 10%, rgba(0, 0, 0, 0.80) 100%);
  border-radius: 1rem;
}

.carousel-item-title {
  @mixin typeface-summary;
  font-weight: 400;
  line-height: 0.8;
  margin: 0;
  z-index: 1;
}

.carousel-item-handle {
  @mixin typeface-regular;
  font-weight: 400;
  color: var(--color-slate-200);
  z-index: 1;
}

.carousel-item-spots {
  @include typeface-detail;
  margin-top: 1rem;
  z-index: 1;
  width: fit-content;
  border-radius: 2rem;
  border: 1px solid var(--color-slate-100);
  background-color: rgba(0,0,0,0.5);
  padding: 0.25rem 1rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    transform: translateX(0);
  }
}

.carousel-item {
  opacity: 0;
  animation: fadeIn 0.5s ease-in forwards;
}

.carousel-item:nth-child(1) { animation-delay: .2s; }
.carousel-item:nth-child(2) { animation-delay: .3s; }
.carousel-item:nth-child(3) { animation-delay: .4s; }
.carousel-item:nth-child(4) { animation-delay: .5s; }
.carousel-item:nth-child(5) { animation-delay: .6s; }
.carousel-item:nth-child(6) { animation-delay: .7s; }
.carousel-item:nth-child(7) { animation-delay: .8s; }
.carousel-item:nth-child(8) { animation-delay: .9s; }
.carousel-item:nth-child(9) { animation-delay: 1s; }
.carousel-item:nth-child(10) { animation-delay: 1.1s; }
.carousel-item:nth-child(11) { animation-delay: 1.2s; }

.featured-creators {
  text-align:center;
}

.skew-graphic {
  display: none;
  @mixin screen sm {
    display: block;
  }
}
</style>
